/**
 * PRESET — "navy"  (BACKUP / previous live scheme)
 * ================================================
 *
 * The dark-navy + Apple-orange scheme that was live before the Apple-dark
 * switch. Values are preserved EXACTLY as they were in `theme/styles.js` so
 * this preset is a true rollback: setting ACTIVE = 'navy' in `presets/index.js`
 * restores the previous appearance with no other edit.
 *
 * Do not "tidy" these values — their contrast ratios were tuned against navy
 * surfaces and are documented inline.
 */

const navy = {
	name: 'navy',
	label: 'Navy (previous)',

	// Dark elevation ramp. Also published as the Chakra `navy` colour scale, so
	// ~1,000 useColorModeValue('…','navy.800') call-sites resolve here.
	surfaceScale: {
		900: '#0B1C2C', // app base
		// Between base and card: the dark value of `bg.subtle`. Without a step
		// here, subtle wells collapsed onto the card colour and every inset
		// surface (zebra rows, section wells, modal footers) went flat in dark.
		850: '#0D2133',
		800: '#10273A', // cards / panels
		700: '#1A3550', // elevated / hover / inputs
		600: '#1E3D5C', // borders / dividers
		500: '#24496E', // strong fills
		400: '#2E5C87', // strong borders / active accents
		300: '#4A7BA3', // info / mid
		200: '#7AAAC4', // disabled foreground
		100: '#B3D0E4', // light tints
		50: '#E8F2F8', // near-white tint
	},

	// Light-mode "paper" ramp — Apple system greys.
	paper: {
		0: '#FFFFFF', // elevated cards
		50: '#FAFAFC', // subtle fills / modal chrome
		100: '#F5F5F7', // app page
		200: '#ECECEF', // subtle borders / hover
		300: '#D2D2D7', // default borders (Apple separator)
		400: '#B0B0B8', // strong borders
		500: '#98989F', // strong UI outline
	},

	// Text. Dark steps are softened (never pure #fff); light steps are Apple labels.
	ink: {
		dPrimary: '#F5F5F7', // 15.9:1 on the base
		dSecondary: '#B7C2CE', // 9.6:1
		dMuted: '#909DAC', // 6.3:1 base / 4.6:1 card
		lPrimary: '#1D1D1F', // Apple label
		lSecondary: '#5B5B5F', // Apple secondary label
		lMuted: '#6E6E73', // Apple tertiary label
	},

	// ONE primary accent — Apple system orange, identical in both modes.
	accent: {
		glow: '#FFB84D',
		light: '#FFB340', // gradient top / dark-mode hover
		primary: '#FF9500', // THE accent
		dark: '#CC7700', // pressed
		hover: '#E68600', // light-mode hover
		onAccent: '#1A1400', // text on an orange fill — never white
		// Accent as INK on light surfaces. #FF9500 is 2.2:1 on white — it fails
		// AA everywhere text or a focus ring uses the accent in light mode, which
		// THEME_GUIDE always said needed a darkened value but never got one.
		// #A45400 keeps the hue and clears AA on every light surface the accent
		// sits on: 5.5:1 on #FFFFFF, 5.0:1 on #F5F5F7, 4.7:1 on #E8E8ED.
		inkLight: '#A45400',
		// Final stop of the accent gradient. A distinct slot because it is NOT
		// `dark` — the gradient ends brighter than the pressed state, and
		// collapsing the two visibly flattens every primary button.
		gradientEnd: '#FF8000',
	},

	/**
	 * The accent as a 10-step Chakra colour SCALE.
	 *
	 * Required in addition to `accent` because ~550 call-sites reference the
	 * accent by scale name — `useColorModeValue('brand.500','brand.400')`,
	 * `colorScheme='brand'`, `bgGradient='linear(…, brand.400, …)'` — and those
	 * resolve against `theme.colors.brand`, not against semantic tokens. Without
	 * this ramp, swapping a preset would change every surface but leave the
	 * brand colour behind.
	 *
	 * ONE-primary ramp: 400 === 500, so `useColorModeValue('brand.500',
	 * 'brand.400')` yields the same accent in both modes.
	 */
	accentScale: {
		50: '#FFF4EC', 100: '#FFE3CC', 200: '#FFC79A', 300: '#FFB340', 400: '#FF9500',
		500: '#FF9500', 600: '#E68600', 700: '#CC7700', 800: '#A85F00', 900: '#7A4500',
	},

	feedback: {
		successD: '#30D158', successL: '#157F55',
		warningD: '#FFD60A', warningL: '#8A5A00',
		dangerD: '#FF453A', dangerL: '#C53030',
		infoD: '#5AA9E6', infoL: '#2B6CB0',
	},
};

export default navy;
